import { useGoBack } from 'hooks/useGoBack';
import React, { useCallback, useContext, useState } from 'react';
import { ModalWrapper } from 'components/ModalWindows/ModalWrapper';
import { ModalToConfirm } from 'components/ModalWindows/ModalToConfirm';
import ModalSentSuccessfully from 'components/ModalWindows/ModalSentSuccessfully';
import ModalSentError from 'components/ModalWindows/ModalSentError';
import ModalSentErrorWithInfoForComboGroup from 'components/ModalWindows/ModalSentErrorWithInfoForComboGroup';
import { convertUndefinedFields } from 'utils/helpers';
import axios from 'axios';
import { ServerErrorTypes } from 'types';
import { AppContext, IAppContext } from 'appState/appContext';
import { EditComboGroupContext, IEditComboGroupContext } from '../editComboGroupState/editComboGroupContext';
import { updateComboGroup } from 'backend/comboGroups';

export const ModalWindows: React.FC = () => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { changeTypeOfModalWindow, refetchData, dataForSubmit, typeOfModalWindow, fetchedDishes, fetchedComboItems } =
		useContext<IEditComboGroupContext>(EditComboGroupContext);
	const [dataForModalWithInfo, setDataForModalWithInfo] = useState<{
		translationKey: string;
		names: string[];
	} | null>(null);
	const goBack = useGoBack();

	const handleRedirect = useCallback(() => {
		goBack('/menu/combo-group');
	}, [goBack]);

	const handleConfirm = useCallback(async () => {
		if (dataForSubmit) {
			try {
				changeTypeOfModalWindow(null);
				const convertedData = convertUndefinedFields(dataForSubmit);
				showLoader();
				await updateComboGroup(convertedData);
				changeTypeOfModalWindow('success');
			} catch (err) {
				if (
					axios.isAxiosError(err) &&
					err.response?.data.Type === ServerErrorTypes.COMBO_GROUP_VALIDATION_FAILED
				) {
					const namesForModal = err.response.data.Metadata.dishIds.map(
						(dishId: string) => fetchedDishes.find((item) => item.id === dishId)?.name,
					);
					setDataForModalWithInfo({
						translationKey: 'followingDishesShouldHaveAtLeastOneSelectedRestaurant',
						names: namesForModal,
					});
				} else if (
					axios.isAxiosError(err) &&
					err.response?.data.Type === ServerErrorTypes.COMBO_ITEM_VALIDATION_FAILED
				) {
					const namesForModal = err.response.data.Metadata.comboItems.map(
						(dishId: string) => fetchedComboItems.find((item) => item.id === dishId)?.name,
					);
					setDataForModalWithInfo({
						translationKey: 'followingComboItemsShouldHaveAtLeastOneSelectedRestaurant',
						names: namesForModal,
					});
				} else {
					changeTypeOfModalWindow('error');
				}
			} finally {
				hideLoader();
			}
		}
	}, [dataForSubmit]);

	const handleUpdateData = useCallback(() => {
		refetchData(true);
		changeTypeOfModalWindow(null);
	}, []);

	const handleCloseModal = useCallback(() => {
		changeTypeOfModalWindow(null);
	}, []);

	const handleCloseModalWithInfo = useCallback(() => {
		setDataForModalWithInfo(null);
	}, []);

	return (
		<ModalWrapper isVisible={!!typeOfModalWindow || !!dataForModalWithInfo}>
			<ModalToConfirm
				onConsentEvent={handleRedirect}
				onFailureEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'toConfirmFieldsClearing'}
				description="cancelSaveInfo"
			/>
			<ModalToConfirm
				onConsentEvent={handleConfirm}
				onFailureEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'toConfirmSubmit'}
				description="saveInfo"
			/>
			<ModalSentSuccessfully
				onConsentEvent={handleUpdateData}
				isVisible={typeOfModalWindow === 'success'}
				description="savedSuccessfully"
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'error'}
				description="errorWhileSaving"
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'defaultTranslationIsEmpty'}
				description="errorDefaultTranslation"
			/>
			<ModalSentErrorWithInfoForComboGroup
				onConsentEvent={handleCloseModalWithInfo}
				data={dataForModalWithInfo}
			/>
		</ModalWrapper>
	);
};
